import { useCallback } from 'react';
import { toast } from 'sonner';
import { User } from 'firebase/auth';
import {
  AppData,
  Task as TaskType,
  resetDailyTasks,
  getTodayString,
  getCurrentTimestamp,
  isTaskLinkedToAchievement,
  updateTaskCompletionCount,
  updateStarHistory,
  updateWalletData,
  calculateDailyTaskStats,
  createActivityLogEntry,
  addActivityLogEntry
} from '@/utils/storage';
import { NativeFeatures } from '@/services/nativeFeatures';

export const useTaskManager = (
  data: AppData | null,
  updateData: (newData: AppData) => Promise<AppData>,
  setIsAnimating: (isAnimating: boolean) => void,
  setShowGiantStar: (showGiantStar: boolean) => void,
  user: User | null,
  onPerfectDay?: (theme: string) => void
) => {
  const handleCompleteTask = useCallback((task: TaskType) => {
    if (!data || task.completed) {
      return;
    }
    
    // Start star animation
    setIsAnimating(true);
    setShowGiantStar(true);
    
    // Trigger haptic feedback for successful task completion
    NativeFeatures.hapticSuccess();
    
    // Update task data immediately
    const updatedTask = updateTaskCompletionCount(task);
    
    const updatedTasks = data.tasks.map(t => {
      if (t.id === task.id) {
        const completedTask = { 
          ...updatedTask, 
          completed: true, 
          lastCompleted: getTodayString(),
          completedDate: getCurrentTimestamp()
        };
        
        if (t.resetInterval) {
          completedTask.lastReset = getCurrentTimestamp();
        }
        
        return completedTask;
      }
      return t;
    });
    
    const newStarCount = data.stars + task.stars;
    const newTotalStarsEarned = (data.totalStarsEarned || 0) + task.stars;
    
    let newData: AppData = { 
      ...data, 
      tasks: updatedTasks, 
      stars: newStarCount,
      totalStarsEarned: newTotalStarsEarned
    };
    
    // Update star history
    newData = updateStarHistory(newData, newStarCount);
    
    // Update wallet data if enabled
    newData = updateWalletData(newData, task.stars);

    // Log activity if user is signed in (for family sharing)
    if (user && newData.familyId) {
      const activityEntry = createActivityLogEntry(
        user.uid,
        user.displayName || 'Unknown',
        'task_completed',
        {
          taskTitle: task.title,
          starsEarned: task.stars
        }
      );
      newData = addActivityLogEntry(newData, activityEntry);
    }

    // Check if this completion results in a perfect day
    const today = getTodayString();
    const dailyStats = calculateDailyTaskStats(newData.tasks, today);

    // If this was the last task to complete a perfect day, trigger celebration
    const wasAlreadyPerfect = data.starHistory?.find(entry => entry.date === today)?.isPerfectDay;
    if (dailyStats.isPerfectDay && !wasAlreadyPerfect && onPerfectDay) {
      // Delay the celebration to let the star animation complete
      setTimeout(() => {
        onPerfectDay(data.theme);
      }, 2000);
    }

    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData, setIsAnimating, setShowGiantStar, user]);

  const handleUpdateStars = useCallback((taskId: string, change: number) => {
    if (!data) {
      return;
    }
    
    const updatedTasks = data.tasks.map(task => {
      if (task.id === taskId) {
        const newStarValue = Math.max(1, task.stars + change);
        return { ...task, stars: newStarValue };
      }
      return task;
    });
    
    const newData: AppData = { ...data, tasks: updatedTasks };
    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData]);

  const handleMoveTask = useCallback((taskId: string, direction: 'up' | 'down') => {
    if (!data) {
      return;
    }
    
    const taskIndex = data.tasks.findIndex(task => task.id === taskId);
    if (taskIndex === -1) {
      return;
    }
    
    if (direction === 'up' && taskIndex === 0) {
      return;
    }
    if (direction === 'down' && taskIndex === data.tasks.length - 1) {
      return;
    }
    
    const newIndex = direction === 'up' ? taskIndex - 1 : taskIndex + 1;
    
    const updatedTasks = [...data.tasks];
    const currentTask = updatedTasks[taskIndex];
    const swapTask = updatedTasks[newIndex];
    
    const tempOrder = currentTask.order;
    currentTask.order = swapTask.order;
    swapTask.order = tempOrder;
    
    updatedTasks.sort((a, b) => (a.order || 0) - (b.order || 0));
    
    const newData: AppData = {
      ...data,
      tasks: updatedTasks
    };
    
    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData]);

  const handleResetDailyTasks = useCallback(() => {
    if (!data) {
      return;
    }
    
    const newData = resetDailyTasks(data);
    
    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData]);

  const handleDeleteTask = useCallback((taskId: string) => {
    if (!data) {
      return;
    }
    
    if (isTaskLinkedToAchievement(taskId, data.achievements)) {
      toast.error("This task cannot be deleted because it's linked to a trophy. Delete the trophy first.");
      return;
    }
    
    const updatedTasks = data.tasks.filter(t => t.id !== taskId);
    const newData: AppData = {
      ...data,
      tasks: updatedTasks
    };
    
    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData]);

  const getTotalDailyStars = useCallback(() => {
    if (!data) return 0;
    
    return data.tasks
      .filter(task => task.daily && !task.completed)
      .reduce((sum, task) => sum + task.stars, 0);
  }, [data]);
  
  const getCompletedTasksCount = useCallback(() => {
    if (!data) return 0;
    return data.tasks.filter(task => task.completed).length;
  }, [data]);
  
  const getUncompletedTasksCount = useCallback(() => {
    if (!data) return 0;
    return data.tasks.filter(task => !task.completed).length;
  }, [data]);

  const handleResetTask = useCallback((task: TaskType) => {
    if (!data) {
      return;
    }

    const updatedTasks = data.tasks.map(t => {
      if (t.id === task.id) {
        return {
          ...t,
          completed: false,
          completedDate: undefined,
          lastReset: getCurrentTimestamp()
        };
      }
      return t;
    });

    let newData: AppData = { ...data, tasks: updatedTasks };

    // Log activity if user is signed in and task was completed (for family sharing)
    if (user && newData.familyId && task.completed) {
      const activityEntry = createActivityLogEntry(
        user.uid,
        user.displayName || 'Unknown',
        'task_uncompleted',
        {
          taskTitle: task.title
        }
      );
      newData = addActivityLogEntry(newData, activityEntry);
    }

    updateData(newData).catch((error) => {
      console.error('[useTaskManager] Failed to persist update:', error);
    });
  }, [data, updateData, user]);

  return {
    handleCompleteTask,
    handleUpdateStars,
    handleMoveTask,
    handleResetDailyTasks,
    handleDeleteTask,
    handleResetTask,
    getTotalDailyStars,
    getCompletedTasksCount,
    getUncompletedTasksCount
  };
};
